import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import TourOverlay from './TourOverlay';
import TourTooltip from './TourTooltip';
import { TourStep, TourData } from './types';
import './styles.css';

interface OnboardingTourProps {
  isOpen: boolean;
  tourData: TourData | null;
  onComplete: () => void;
  onSkip: () => void;
}

const OnboardingTour: React.FC<OnboardingTourProps> = ({
  isOpen,
  tourData: tour,
  onComplete,
  onSkip,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Find and set the target element for the current step
  const updateTargetElement = useCallback(async () => {
    if (!tour || !tour.steps || !tour.steps[currentStep]) return;

    const step = tour.steps[currentStep];

    // Execute beforeStep callback if it exists and wait for it
    if (step.beforeStep) {
      const result = step.beforeStep();
      if (result && typeof result === 'object' && 'then' in result) {
        try {
          await result;
        } catch (error) {
          console.error('OnboardingTour: beforeStep failed', error);
          // If beforeStep fails, skip this step
          if (currentStep < tour.steps.length - 1) {
            setCurrentStep(prev => prev + 1);
          } else {
            onComplete();
          }
          return;
        }
      }
    }

    const target = document.querySelector(step.target) as HTMLElement;
    if (target) {
      setTargetElement(target);

      // Only scroll if element is clipped/not visible, and show top of element
      const rect = target.getBoundingClientRect();
      const headerHeight = 80; // Account for fixed header (adjust if needed)
      const isVisible = (
        rect.top >= headerHeight &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );

      if (!isVisible) {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start', // Show top of element instead of centering
          inline: 'nearest'
        });

        // Adjust scroll position to account for fixed header
        setTimeout(() => {
          window.scrollBy({
            top: -headerHeight,
            behavior: 'smooth'
          });
        }, 100);
      }
    } else {
      console.warn(`OnboardingTour: Target element "${step.target}" not found, skipping to next step`);
      setTargetElement(null);

      // Auto-skip to next step if element not found after a short delay
      setTimeout(() => {
        if (currentStep < tour.steps.length - 1) {
          setCurrentStep(prev => prev + 1);
        } else {
          // If it's the last step and element not found, complete the tour
          onComplete();
        }
      }, 500);
    }
  }, [tour, currentStep, onComplete]);

  // Initialize tour and set up target element
  useEffect(() => {
    if (!isOpen || !tour) {
      setIsVisible(false);
      // Clear active tour ID when tour closes
      localStorage.removeItem('activeTourId');
      return;
    }

    // Store active tour ID in localStorage
    if (tour.id) {
      localStorage.setItem('activeTourId', tour.id);
    }

    updateTargetElement();

    // Show tour after a brief delay to ensure element is rendered
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, [currentStep, updateTargetElement, isOpen, tour]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          handleSkip();
          break;
        case 'ArrowRight':
        case ' ':
          if (tour && currentStep < tour.steps.length - 1) {
            handleNext();
          } else {
            handleComplete();
          }
          break;
        case 'ArrowLeft':
          if (currentStep > 0) {
            handlePrevious();
          }
          break;
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentStep, tour?.steps.length, isVisible]);

  // Execute afterStep callback on cleanup
  useEffect(() => {
    if (!tour || !tour.steps) return;

    const step = tour.steps[currentStep];

    return () => {
      if (step?.afterStep) {
        step.afterStep();
      }
    };
  }, [currentStep, tour?.steps]);

  const handleNext = () => {
    if (tour && currentStep < tour.steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    setIsVisible(false);
    onSkip();
  };

  const handleComplete = () => {
    setIsVisible(false);
    onComplete();
  };

  const handleStepClick = (stepIndex: number) => {
    setCurrentStep(stepIndex);
  };

  // Early return if not open or no tour data
  if (!isOpen || !tour) {
    return null;
  }
  
  if (!tour.steps || !Array.isArray(tour.steps) || tour.steps.length === 0) {
    console.warn('OnboardingTour: Invalid or empty tour steps:', tour);
    return null;
  }

  // Don't render if not visible or no target element
  if (!isVisible || !targetElement) {
    return null;
  }

  // Ensure currentStep is within bounds
  const safeCurrentStep = Math.max(0, Math.min(currentStep, tour.steps.length - 1));
  const currentStepData = tour.steps[safeCurrentStep];
  
  if (!currentStepData) {
    return null;
  }

  return createPortal(
    <>
      <TourOverlay
        targetElement={targetElement}
        allowInteraction={currentStepData.allowInteraction}
      />
      <TourTooltip
        step={currentStepData}
        currentStep={safeCurrentStep}
        totalSteps={tour.steps.length}
        targetElement={targetElement}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onSkip={handleSkip}
        onComplete={handleComplete}
        onStepClick={handleStepClick}
        canGoNext={safeCurrentStep < tour.steps.length - 1}
        canGoPrevious={safeCurrentStep > 0}
        isLastStep={safeCurrentStep === tour.steps.length - 1}
      />
    </>,
    document.body
  );
};

export default OnboardingTour;